import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../hooks/useAuth";
import UserPage from "./UserPage";
import AdminPage from "./AdminPage";
import UsageDashboard from "./UsageDashboard";

const ThemeToggleButton = ({ theme, toggleTheme }) => (
  <button
    onClick={toggleTheme}
    style={{
      background: "none",
      border: "none",
      color: "var(--text-color)", // Changed to text-color for better visibility in menu
      fontSize: "0.7rem",
      cursor: "pointer",
      padding: "0.25rem",
      lineHeight: "1",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%" // Full width for menu item
    }}
    aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
  >
    {theme === "dark" ? "☀️ Light Mode" : "🌙 Dark Mode"}
  </button>
);


function UserSettingsMenu({ theme, toggleTheme, selectedApis, setSelectedApis, refreshHistories, resetMainPanel, refreshModelSettings, getPanelRight }) {
  const { user, refreshUser, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [showUserPage, setShowUserPage] = useState(false);
  const [showAdminPage, setShowAdminPage] = useState(false);
  const [showUsagePage, setShowUsagePage] = useState(false);
  const [userSettingsHover, setUserSettingsHover] = useState(false);
  const [adminHover, setAdminHover] = useState(false);
  const [usageHover, setUsageHover] = useState(false);
  const [logoutHover, setLogoutHover] = useState(false);
  const ref = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handler = e => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Compute fixed dropdown position from button bounding rect so the menu
  // escapes any overflow:auto/hidden ancestor (e.g. the sidebar panel).
  const handleMenuToggle = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const menuHeight = 280; // approximate
      // Clamp popup width so its right edge never exceeds the panel's right edge.
      const panelRight = getPanelRight ? getPanelRight() : null;
      const maxWidth = panelRight ? Math.min(236, panelRight - rect.left - 8) : 236;
      const vertical = spaceBelow < menuHeight
        ? { bottom: window.innerHeight - rect.top + 4, top: "auto" }
        : { top: rect.bottom + 4, bottom: "auto" };
      setDropdownPos({ ...vertical, left: rect.left, width: maxWidth });
    }
    setOpen(o => {
      const newOpenState = !o;
      if (newOpenState && refreshUser) refreshUser();
      return newOpenState;
    });
  };

  if (!user) return null;

  // Extract username from email (everything before @)
  const username = user.email ? user.email.split('@')[0] : "User";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={handleMenuToggle}
        style={{
          cursor: "pointer",
          background: "none",
          border: "none",
          fontSize: "1.7rem",
          padding: 0,
          display: "flex",
          alignItems: "center"
        }}
        aria-label="User menu"
      >
        {/* SVG user icon */}
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--user-icon-color)"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ display: "block" }}
          aria-hidden="true"
        >
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20c0-3.3 2.7-6 6-6h4c3.3 0 6 2.7 6 6" />
        </svg>
      </button>
      {open && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "fixed",
            top: dropdownPos.top,
            bottom: dropdownPos.bottom,
            left: dropdownPos.left,
            background: "var(--panel-background)",
            border: "1px solid var(--panel-border)",
            padding: "0.6rem",
            zIndex: 9999,
            width: dropdownPos.width ?? 236,
            maxWidth: "calc(100vw - 24px)",
            borderRadius: "6px",
            boxShadow: "var(--modal-shadow)",
            color: "var(--text-color)"
          }}
        >
          <div style={{ fontSize: "0.9rem", marginBottom: "0.6rem", fontWeight: "bold", textAlign: "center", borderBottom: "1px solid var(--panel-border)", paddingBottom: "0.4rem" }}>
              {username}
          </div>

          {/* Theme Toggle */}
          <div style={{ marginBottom: "0.75rem", display: "flex", justifyContent: "center" }}>
             <ThemeToggleButton theme={theme} toggleTheme={toggleTheme} />
          </div>

          <div style={{ borderTop: "1px solid var(--panel-border)", paddingTop: "0.75rem" }}>
              <button
                onClick={() => {
                  setShowUserPage(true);
                  setOpen(false);
                }}
                onMouseEnter={() => setUserSettingsHover(true)}
                onMouseLeave={() => setUserSettingsHover(false)}
                style={{
                  width: "100%",
                  background: userSettingsHover ? "var(--button-hover-background)" : "var(--button-background)",
                  color: "var(--button-text)",
                  border: "none",
                  padding: "0.5rem",
                  cursor: "pointer",
                  borderRadius: "4px",
                  marginBottom: "0.5rem",
                  fontWeight: "bold"
                }}
              >
                User Settings
              </button>
              {!!user.is_admin && (
                <button
                  onClick={() => {
                    setShowAdminPage(true);
                    setOpen(false);
                  }}
                  onMouseEnter={() => setAdminHover(true)}
                  onMouseLeave={() => setAdminHover(false)}
                  style={{
                    width: "100%",
                    background: adminHover ? "#e67e22" : "#f39c12",
                    color: "#fff",
                    border: "none",
                    padding: "0.5rem",
                    cursor: "pointer",
                    borderRadius: "4px",
                    marginBottom: "0.5rem",
                    fontWeight: "bold"
                  }}
                >
                  🛡️ Admin Panel
                </button>
              )}
              {!user.is_admin && (
                <button
                  onClick={() => {
                    setShowUsagePage(true);
                    setOpen(false);
                  }}
                  onMouseEnter={() => setUsageHover(true)}
                  onMouseLeave={() => setUsageHover(false)}
                  style={{
                    width: "100%",
                    background: usageHover ? "#e67e22" : "#f39c12",
                    color: "#fff",
                    border: "none",
                    padding: "0.5rem",
                    cursor: "pointer",
                    borderRadius: "4px",
                    marginBottom: "0.5rem",
                    fontWeight: "bold"
                  }}
                >
                  My Usage
                </button>
              )}
              <button
                onClick={logout}
                onMouseEnter={() => setLogoutHover(true)}
                onMouseLeave={() => setLogoutHover(false)}
                style={{
                  width: "100%",
                  background: logoutHover ? "#c82333" : "#e5393f",
                  color: "#fff",
                  border: "none",
                  padding: "0.5rem",
                  cursor: "pointer",
                  borderRadius: "4px",
                  fontWeight: "bold"
                }}
              >
                Logout
              </button>
          </div>
        </div>
      , document.body)}
      {showUserPage && createPortal(
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.5)",
            zIndex: 2001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setShowUserPage(false)}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
            onClick={e => e.stopPropagation()}
          >
            <UserPage refreshHistories={refreshHistories} resetMainPanel={resetMainPanel} refreshModelSettings={refreshModelSettings} />
            <button
              onClick={() => setShowUserPage(false)}
              style={{
                marginTop: "1rem",
                background: "var(--panel-background)",
                color: "var(--text-color)",
                border: "none",
                padding: "0.7rem 1.2rem",
                cursor: "pointer",
                borderRadius: "6px",
                fontWeight: 600,
                fontSize: '0.95rem'
              }}
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
      {showUsagePage && createPortal(
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.5)",
            zIndex: 2001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: '1rem'
          }}
          onClick={() => setShowUsagePage(false)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '1200px',
              background: 'var(--panel-background)',
              borderRadius: '8px',
              maxHeight: '85vh',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              display: 'flex',
              flexDirection: 'column',
              margin: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ flex: '1 1 auto', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <UsageDashboard />
            </div>
            <div style={{
              padding: '1rem',
              textAlign: 'center',
              borderTop: '1px solid var(--panel-border)',
              flexShrink: 0
            }}>
              <button
                onClick={() => setShowUsagePage(false)}
                style={{
                  background: "var(--panel-background)",
                  color: "var(--text-color)",
                  border: "1px solid var(--panel-border)",
                  padding: "0.7rem 1.5rem",
                  cursor: "pointer",
                  borderRadius: "6px",
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {showAdminPage && createPortal(
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.5)",
            zIndex: 2001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: '1rem'
          }}
          onClick={() => setShowAdminPage(false)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '1800px',
              background: 'var(--panel-background)',
              borderRadius: '8px',
              maxHeight: '85vh',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              display: 'flex',
              flexDirection: 'column',
              margin: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ flex: '1 1 auto', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <AdminPage />
            </div>
            <div style={{
              padding: '1rem',
              textAlign: 'center',
              borderTop: '1px solid var(--panel-border)',
              flexShrink: 0
            }}>
              <button
                onClick={() => setShowAdminPage(false)}
                style={{
                  background: "var(--panel-background)",
                  color: "var(--text-color)",
                  border: "1px solid var(--panel-border)",
                  padding: "0.7rem 1.5rem",
                  cursor: "pointer",
                  borderRadius: "6px",
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}
              >
                Close Admin Panel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default React.memo(UserSettingsMenu);
